import type {
  PropertyValueItem,
  WorkflowTaskDetail,
} from "../services/regulationsService";
import {
  resolvePreviousTaskFromTaskDetail,
  resolveSourceFieldsFromTaskDetail,
} from "./workflowTaskDetailNormalize";
import {
  buildTaskOwnerStructureFromIds,
  extractIdsFromTaskOwnerStructure,
  extractTaskOwnersListFromResponse,
} from "../components/FormRenderer/formFieldUtils";
import { getHostApiBaseUrl } from "../config/env";
import { apiClient, getAuthorizationHeader, getDomain } from "../services/apiClient";
import { getMinimumSelectableDateApi } from "./manualAssessmentTaskTable";

/** Options for async/static fields keyed by field name (same as FormRenderer). */
export type OptionsByField = Record<
  string,
  Array<{ code: string; name: string }>
>;

type PrettifiedTFormInput = PropertyValueItem & {
  field: string;
  type: string;
  order: number;
  asyncConfig?: Record<string, unknown>;
  textArea?: boolean;
  options?: Array<{ label: string; value: string | number }>;
  configurability: boolean;
  readonly: boolean;
  disabled: boolean;
  minDate?: string;
};

/** Field names that are always read-only in DynamicForm (not editable regardless of backend). */
const NON_EDITABLE_OWNER_FIELDS = ["taskOwners", "taskAssignee"];

/** Labels excluded from previous-task read-only dynamic form only (label/originlabel, case-insensitive). */
const HIDDEN_PREVIOUS_TASK_DYNAMIC_FORM_LABELS = new Set(["email notification"]);

/** Keys for task-owner style fieldValue: { taskUsers: id[], taskGroups: id[], taskRoles: id[] } */
const TASK_OWNER_GROUP_KEYS = ["taskUsers", "taskGroups", "taskRoles"] as const;

/** Dynamic Form only renders text inputs as `type: "text"` (+ optional `textArea`). */
export function normalizeDynamicFormTextFieldType(item: {
  type?: unknown;
  textArea?: unknown;
  textarea?: unknown;
}): { type: string; textArea: boolean } {
  const backendType = item.type != null ? String(item.type).trim() : "text";
  const typeLower = backendType.toLowerCase();
  const textArea =
    item.textArea === true ||
    item.textarea === true ||
    typeLower === "textarea" ||
    typeLower === "textare";

  if (
    textArea ||
    typeLower === "text" ||
    typeLower === "string" ||
    typeLower === "textarea" ||
    typeLower === "textare"
  ) {
    return { type: "text", textArea };
  }

  return { type: backendType || "text", textArea: false };
}

/**
 * Freyr Dynamic Form character counter / html maxLength only read `maxLen`.
 * Pre-init workflowFields (e.g. originalTitle) may send `maxLength` instead.
 */
export function resolveDynamicFormMaxLen(item: {
  maxLen?: unknown;
  maxLength?: unknown;
}): number | undefined {
  const candidates = [item.maxLen, item.maxLength];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate) && candidate > 0) {
      return candidate;
    }
    if (typeof candidate === "string" && candidate.trim() !== "") {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  return undefined;
}

/** Check if value is task-owner structure { taskUsers, taskGroups, taskRoles } */
function isTaskOwnerStructure(v: unknown): v is Record<string, unknown> {
  if (v == null || typeof v !== "object" || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return "taskUsers" in o || "taskGroups" in o || "taskRoles" in o;
}

/** Collect IDs from task-owner style fieldValue: { taskUsers, taskGroups, taskRoles } */
function fieldValueToIds(v: unknown): string[] {
  if (v === null || v === undefined) return [];
  if (typeof v === "string") return v ? [v] : [];
  if (typeof v === "number") return [String(v)];
  if (typeof v === "object" && v !== null && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of TASK_OWNER_GROUP_KEYS) {
      const arr = o[key];
      if (Array.isArray(arr)) {
        for (const id of arr) {
          if (id != null && id !== "") parts.push(String(id));
        }
      }
    }
    return parts;
  }
  return [];
}

/**
 * Convert fieldValue to a single display string for taskOwners/taskAssignee text input.
 * - string/number: return as-is (string).
 * - object with taskUsers/taskGroups/taskRoles: collect all IDs from non-empty arrays and join with ", ".
 * - otherwise: empty string.
 */
function fieldValueToDisplayString(v: unknown): string {
  return fieldValueToIds(v).join(", ");
}

export interface TaskDetailDynamicFormConfig {
  /** Definition array consumed by DynamicFormCompBasicJsonSchema */
  jsonData: PrettifiedTFormInput[];
  /** Initial values consumed by DynamicFormCompBasicJsonSchema */
  initialFormData: Record<string, unknown>;
}

/** Which propertyValueItemList rows feed a DynamicForm instance. */
export type DynamicFormVisibilityMode =
  | "editable"
  | "preInit"
  | "readonlyHidden"
  | "readonlyPrevious"
  | "assignNextTask";

function isItemHidden(item: PropertyValueItem): boolean {
  const rawHide = (item as Record<string, unknown>).hide;
  return (
    rawHide === true ||
    rawHide === 1 ||
    (typeof rawHide === "string" && rawHide.toLowerCase() === "true")
  );
}

function parseBooleanish(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === "true") return true;
  if (value === false || value === 0 || value === "false") return false;
  return undefined;
}

/**
 * Regulation Details task form display flags from PT Task Properties.
 * Common Dynamic Form only filters `hide`; apply `visible` + `detailsPage.visibility` here.
 */
function isShownOnRegulationDetailsTaskForm(item: PropertyValueItem): boolean {
  const raw = item as Record<string, unknown>;
  if (parseBooleanish(raw.visible) === false) return false;

  const detailsPage = raw.detailsPage as { visibility?: unknown } | undefined;
  if (detailsPage && typeof detailsPage === "object") {
    if (parseBooleanish(detailsPage.visibility) === false) return false;
  }

  return true;
}

/**
 * Previous-task modal/inline form: same display gates as the editable task form —
 * `hide`, root `visible`, and `detailsPage.visibility`.
 */
function isShownOnPreviousTaskForm(item: PropertyValueItem): boolean {
  return isShownOnRegulationDetailsTaskForm(item);
}

function isExcludedFromPreviousTaskDynamicForm(
  item: PropertyValueItem,
): boolean {
  for (const raw of [item.label, item.originlabel]) {
    if (typeof raw !== "string") continue;
    const normalized = raw.trim().toLowerCase();
    if (normalized && HIDDEN_PREVIOUS_TASK_DYNAMIC_FORM_LABELS.has(normalized)) {
      return true;
    }
  }
  return false;
}

/**
 * Previous-task modal title from `previousTask.taskInstanceName`
 * (e.g. "Previous Task - Overall Review").
 */
export function resolvePreviousTaskDialogTitle(
  taskDetail: WorkflowTaskDetail | null | undefined,
  fallback = "View previous task",
): string {
  const previousTask = resolvePreviousTaskFromTaskDetail(taskDetail);
  const instanceName =
    typeof previousTask?.taskInstanceName === "string"
      ? previousTask.taskInstanceName.trim()
      : "";
  if (!instanceName) return fallback;
  return `Previous Task - ${instanceName}`;
}

function isPreInitExcludedField(item: PropertyValueItem): boolean {
  const field = item.field != null ? String(item.field).trim() : "";
  return !field || field === "dueDate";
}

function isAttachmentField(item: PropertyValueItem): boolean {
  return String(item.type ?? "").trim().toLowerCase() === "attachment";
}

function normalizeAttachmentFileMaxSize(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Attachment upload/download hit `{host}/pm/file/…`, not `/gri-service`.
 * Backend sends `prefixEnvName: REACT_APP_API_GETWAY` (CRA); Vite has no such env —
 * set `basicUrl` like asyncConfig so CustomAttachmentWidget can resolve URLs.
 */
function normalizeAttachmentRequestConfig(
  config: unknown,
): Record<string, unknown> | undefined {
  if (config == null || typeof config !== "object" || Array.isArray(config)) {
    return undefined;
  }
  const raw = config as Record<string, unknown>;
  const url = typeof raw.url === "string" ? raw.url : "";
  if (!url.startsWith("/pm/")) return { ...raw };

  return {
    ...raw,
    basicUrl:
      typeof raw.basicUrl === "string" && raw.basicUrl.length > 0
        ? raw.basicUrl
        : getHostApiBaseUrl(),
  };
}

function filterListByVisibilityMode(
  list: PropertyValueItem[],
  mode: DynamicFormVisibilityMode,
): PropertyValueItem[] {
  if (mode === "assignNextTask") {
    return list.filter((item) => {
      const field = item.field != null ? String(item.field).trim() : "";
      return Boolean(field);
    });
  }
  if (mode === "preInit") {
    return list.filter((item) => !isPreInitExcludedField(item));
  }
  if (mode === "readonlyPrevious") {
    return list.filter(
      (item) => !isItemHidden(item) && isShownOnPreviousTaskForm(item),
    );
  }
  if (mode === "editable") {
    return list.filter(
      (item) => !isItemHidden(item) && isShownOnRegulationDetailsTaskForm(item),
    );
  }
  return list.filter((item) => isItemHidden(item));
}

function getPreviousTaskPropertyList(
  taskDetail: WorkflowTaskDetail | null,
): PropertyValueItem[] {
  const list =
    resolvePreviousTaskFromTaskDetail(taskDetail)?.propertyValueItemList;
  return Array.isArray(list) ? list : [];
}

function fieldValueToInitialValue(v: unknown): unknown {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return v;
  if (Array.isArray(v)) return v;
  if (typeof v === "object") return v;
  return undefined;
}

/** Normalize options to { label, value } so convert + CustomSelectWidget can show initial value. */
function normalizeOptions(
  item: PropertyValueItem,
  optionsByField?: OptionsByField,
): Array<{ label: string; value: string | number }> {
  if (Array.isArray(item.options) && item.options.length > 0) {
    return item.options.map((o) => {
      const raw = o as Record<string, unknown>;
      let label: string;
      if (typeof raw.label === "string") label = raw.label;
      else if (typeof raw.name === "string") label = raw.name;
      else {
        const v = raw.value ?? raw.code;
        label = typeof v === "string" || typeof v === "number" ? String(v) : "";
      }
      const value = (raw.value ?? raw.code) as string | number;
      return { label, value };
    });
  }
  const fieldName = item.field ? String(item.field) : "";
  const injected = optionsByField?.[fieldName];
  if (Array.isArray(injected) && injected.length > 0) {
    return injected.map((o) => ({ label: o.name || o.code, value: o.code }));
  }
  return [];
}

function normalizeAsyncConfig(item: PropertyValueItem): Record<string, unknown> | undefined {
  const asyncConfig = (item as Record<string, unknown>).asyncConfig as
    | Record<string, unknown>
    | undefined;
  if (!asyncConfig) return undefined;

  const url = typeof asyncConfig.url === "string" ? asyncConfig.url : "";
  // global_filter_search: common `useCustomSelect` treats list as relative to `axiosResponse.data`
  // (the API body). Typical shape is `{ code: 200, data: [...] }` where `data` is the array.
  // Do NOT default `listDataPath` to `"data"` — the hook already uses `resData.data` as the
  // base for `get(..., listDataPath)`; setting `"data"` would resolve like `get(array, "data")`
  // and yield [], so async options (and department labels) never load.
  // Only pass `listDataPath` when the array is nested (e.g. `data.records`).
  if (url.includes("/knowledge_manager/concepts/global_filter_search")) {
    return { ...asyncConfig };
  }

  if (!url.startsWith("/pm/")) return asyncConfig;

  // PM selection APIs live at {host}/pm/…, not under /gri-service (avoid …/gri-service/pm/…).
  return {
    ...asyncConfig,
    basicUrl:
      typeof asyncConfig.basicUrl === "string" && asyncConfig.basicUrl.length > 0
        ? asyncConfig.basicUrl
        : getHostApiBaseUrl(),
  };
}

function isFalseFlag(v: unknown): boolean {
  return v === false || v === 0 || v === "false";
}

function shouldDisableField(
  item: PropertyValueItem,
  forceOwnerReadonly: boolean,
): boolean {
  if (forceOwnerReadonly) return true;
  if (item.disabled === true || item.readonly === true) return true;
  return isFalseFlag((item as Record<string, unknown>).configurability);
}

function shouldPreserveEmptyString(item: PropertyValueItem): boolean {
  const { type, textArea } = normalizeDynamicFormTextFieldType(
    item as PropertyValueItem & { textarea?: boolean },
  );
  return type === "text" || textArea;
}

function shouldRenderAsDisplayText(
  field: string,
  backendType: string,
  disableField: boolean,
): boolean {
  // We intentionally do NOT convert disabled selects (including department) to text,
  // so common package can still use select widgets + async options to resolve labels.
  return (
    typeof field === "string" &&
    typeof backendType === "string" &&
    typeof disableField === "boolean" &&
    false
  );
}

function itemToDynamicFormInput(
  item: PropertyValueItem,
  index: number,
  optionsByField?: OptionsByField,
  mode: DynamicFormVisibilityMode = "editable",
): PrettifiedTFormInput | null {
  if (mode === "readonlyPrevious" && isExcludedFromPreviousTaskDynamicForm(item)) {
    return null;
  }

  const hide = isItemHidden(item);
  const attachmentField = isAttachmentField(item);
  if (mode === "assignNextTask") {
    // assign-next fields are explicitly scoped by the backend payload.
  } else if (mode === "preInit") {
    if (isPreInitExcludedField(item)) return null;
  } else if (mode === "editable" && hide && !attachmentField) {
    return null;
  } else if (mode === "readonlyHidden" && !hide) {
    return null;
  } else if (mode === "readonlyPrevious" && hide) {
    return null;
  }
  if (mode === "readonlyPrevious") {
    if (!isShownOnPreviousTaskForm(item)) return null;
  } else if (
    mode !== "readonlyHidden" &&
    mode !== "preInit" &&
    mode !== "assignNextTask" &&
    !isShownOnRegulationDetailsTaskForm(item)
  ) {
    return null;
  }

  const field = item.field != null ? String(item.field) : `field_${index}`;
  const isOwnerField = NON_EDITABLE_OWNER_FIELDS.includes(field);
  const forceOwnerReadonly =
    isOwnerField && mode !== "preInit" && mode !== "assignNextTask";
  const order = typeof item.order === "number" ? item.order : index;
  const forceReadonly =
    mode === "readonlyHidden" || mode === "readonlyPrevious";
  const disableField =
    forceReadonly || shouldDisableField(item, forceOwnerReadonly);
  const originalAsyncConfig = normalizeAsyncConfig(item) as
    | { url?: string }
    | undefined;
  const asyncConfig = originalAsyncConfig;
  const hasAsyncOptions = !!asyncConfig?.url;
  const backendType = item.type != null ? String(item.type) : "text";
  const { type: normalizedTextType, textArea: normalizedTextArea } =
    normalizeDynamicFormTextFieldType(
      item as PropertyValueItem & { textarea?: boolean },
    );
  const isTextLike = normalizedTextType === "text" && (
    normalizedTextArea ||
    backendType.toLowerCase() === "text" ||
    backendType.toLowerCase() === "string"
  );
  const type =
    shouldRenderAsDisplayText(field, backendType, disableField)
      ? "text"
      : forceOwnerReadonly && !hasAsyncOptions
      ? "text"
      : isTextLike || normalizedTextArea
      ? normalizedTextType
      : backendType;
  const textArea =
    normalizedTextArea ||
    (item as Record<string, unknown>).textArea === true ||
    (item as Record<string, unknown>).textarea === true;
  const maxLen = resolveDynamicFormMaxLen(
    item as PropertyValueItem & { maxLength?: unknown },
  );

  // When a field has asyncConfig.url, options must come only from the async request (e.g. global_filter_search, task-owners, task-assignees).
  const options = normalizeOptions(item, optionsByField);

  const attachmentFileMaxSize = normalizeAttachmentFileMaxSize(
    (item as Record<string, unknown>).fileMaxSize,
  );
  const isDateField = String(type).trim().toLowerCase() === "date";
  const editableDateMinDate =
    isDateField && !disableField ? getMinimumSelectableDateApi() : undefined;

  if (attachmentField) {
    const itemRecord = item as Record<string, unknown>;
    return {
      ...item,
      field,
      type: "attachment",
      order,
      hide: false,
      field_xs: 12,
      field_md: 12,
      field_lg: 12,
      field_xl: 12,
      ...(attachmentFileMaxSize != null ? { fileMaxSize: attachmentFileMaxSize } : {}),
      uploadConfig: normalizeAttachmentRequestConfig(itemRecord.uploadConfig),
      downloadConfig: normalizeAttachmentRequestConfig(itemRecord.downloadConfig),
      configurability:
        forceReadonly || forceOwnerReadonly ? false : !disableField,
      readonly: disableField,
      disabled: disableField,
    } as PrettifiedTFormInput;
  }

  // Keep all extra keys from backend; override type/options for owner fields so they render as input.
  // When hasAsyncOptions, force options to [] so ...item.options (from backend) is overridden and Dynamic Form uses only async options.
  // readonlyHidden rows: clear `hide` on the payload — common package ConvertJsonSchemaFunc filters `!el.hide` again.
  return {
    ...item,
    field,
    type,
    order,
    ...(forceReadonly || mode === "assignNextTask" ? { hide: false } : {}),
    asyncConfig,
    textArea,
    ...(maxLen != null ? { maxLen } : {}),
    ...(options.length > 0 ? { options } : {}),
    ...(attachmentFileMaxSize != null ? { fileMaxSize: attachmentFileMaxSize } : {}),
    ...(editableDateMinDate ? { minDate: editableDateMinDate } : {}),
    configurability:
      forceReadonly || forceOwnerReadonly ? false : !disableField,
    readonly: disableField,
    disabled: disableField,
  } as PrettifiedTFormInput;
}

function buildTaskDetailDynamicFormConfig(
  list: PropertyValueItem[],
  optionsByField?: OptionsByField,
  mode: DynamicFormVisibilityMode = "editable",
): TaskDetailDynamicFormConfig {
  const jsonData: PrettifiedTFormInput[] = [];
  const initialFormData: Record<string, unknown> = {};

  list.forEach((item, index) => {
    const key = item.field != null ? String(item.field) : `field_${index}`;
    const config = itemToDynamicFormInput(item, index, optionsByField, mode);
    if (!config) return;

    const val = getInitialValueForItem(item, key, optionsByField);
    if (val !== undefined) initialFormData[key] = val;
    jsonData.push(config);
  });

  const scopedList = filterListByVisibilityMode(list, mode);
  const finalInitialFormData = applyExplicitDepartmentAndTaskResultInitials(
    initialFormData,
    scopedList,
    optionsByField,
  );

  return {
    jsonData,
    initialFormData: finalInitialFormData,
  };
}

function isCustomGroupField(item: PropertyValueItem): boolean {
  const groupConfig = (item as { groupConfig?: { isCustomGroup?: boolean } })
    .groupConfig;
  return groupConfig?.isCustomGroup === true;
}

function parseMultipleFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "true";
}

/** Split multi-select `fieldValue` (array or comma-joined string) into option values. */
function splitMultiSelectFieldValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry).trim())
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }
  return [];
}

function getInitialValueForItem(
  item: PropertyValueItem,
  key: string,
  optionsByField?: OptionsByField,
): unknown {
  const isOwnerField = NON_EDITABLE_OWNER_FIELDS.includes(key);
  const asyncConfig = normalizeAsyncConfig(item) as { url?: string } | undefined;
  const hasAsyncOptions = !!asyncConfig?.url;

  if (isOwnerField && hasAsyncOptions) {
    if (isTaskOwnerStructure(item.fieldValue)) {
      return item.fieldValue;
    }
    const ids = fieldValueToIds(item.fieldValue);
    if (ids.length === 0) return undefined;

    if (isCustomGroupField(item)) {
      const multiple = parseMultipleFlag(item.multiple);
      return buildTaskOwnerStructureFromIds(multiple ? ids : [ids[0]]);
    }

    const multiple = parseMultipleFlag(item.multiple);
    if (multiple) {
      return ids;
    }
    return ids[0];
  }

  if (isOwnerField) {
    return fieldValueToDisplayString(item.fieldValue);
  }

  if (isAttachmentField(item)) {
    return Array.isArray(item.fieldValue) ? item.fieldValue : [];
  }

  // Keep department as select + asyncConfig (common package will resolve label from async options).

  if (item.fieldValue == null) {
    return shouldPreserveEmptyString(item) ? "" : undefined;
  }

  // Multi-select (e.g. taskResult): CustomSelectWidget matches options against an array.
  // Backend often stores `"Reject:…,Reopen:…"` — pass split values or labels won't resolve.
  const typeLower = String(item.type ?? "").trim().toLowerCase();
  if (
    (typeLower === "select" || typeLower === "multiselect") &&
    parseMultipleFlag(item.multiple)
  ) {
    const values = splitMultiSelectFieldValues(item.fieldValue);
    return values.length > 0 ? values : undefined;
  }

  return fieldValueToInitialValue(item.fieldValue);
}

/**
 * Re-apply `department` and `taskResult` only from their own PropertyValueItem rows.
 * - `taskResult` (enum value e.g. assessment_required) must come from field `taskResult`, not from `assessment_required` (yes/no).
 * - Idempotent with the main loop but makes the contract obvious for RJSF/schema alignment.
 */
function applyExplicitDepartmentAndTaskResultInitials(
  initialFormData: Record<string, unknown>,
  list: PropertyValueItem[],
  optionsByField?: OptionsByField,
): Record<string, unknown> {
  const out = { ...initialFormData };
  for (const item of list) {
    const field = item.field != null ? String(item.field) : "";
    if (field !== "department" && field !== "taskResult") continue;
    const val = getInitialValueForItem(item, field, optionsByField);
    if (val !== undefined) {
      out[field] = val;
    }
  }
  return out;
}

/**
 * Editable dynamic form: `hide !== true` fields from propertyValueItemList.
 */
export function taskDetailToDynamicFormConfig(
  taskDetail: WorkflowTaskDetail | null,
  optionsByField?: OptionsByField,
): TaskDetailDynamicFormConfig {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list) || list.length === 0) {
    return { jsonData: [], initialFormData: {} };
  }

  return buildTaskDetailDynamicFormConfig(list, optionsByField, "editable");
}

/** Build DynamicForm config from an arbitrary propertyValueItemList slice. */
export function propertyValueItemsToDynamicFormConfig(
  list: PropertyValueItem[],
  optionsByField?: OptionsByField,
  mode: DynamicFormVisibilityMode = "editable",
): TaskDetailDynamicFormConfig {
  if (!Array.isArray(list) || list.length === 0) {
    return { jsonData: [], initialFormData: {} };
  }
  return buildTaskDetailDynamicFormConfig(list, optionsByField, mode);
}

/**
 * Pre-init dialog: same as editable, but `taskOwners` / `taskAssignee` follow backend
 * editability (not forced read-only like on the regulation task detail page).
 */
export function taskDetailToPreInitDynamicFormConfig(
  taskDetail: WorkflowTaskDetail | null,
  optionsByField?: OptionsByField,
): TaskDetailDynamicFormConfig {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list) || list.length === 0) {
    return { jsonData: [], initialFormData: {} };
  }

  return buildTaskDetailDynamicFormConfig(list, optionsByField, "preInit");
}

/** Passed to common package DynamicForm `extraState` (e.g. attachment upload params). */
export function buildDynamicFormExtraState(
  taskDetail: WorkflowTaskDetail | null,
): Record<string, unknown> {
  const id = taskDetail?.task?.taskInstanceUniqueId;
  if (id == null || String(id).trim() === "") {
    return {};
  }
  return { taskInstanceUniqueId: String(id) };
}

/**
 * Previous-task attachment download/upload params must use the prior step's
 * `taskInstanceUniqueId` (downloadConfig.params store=extraState), not the
 * current task's id.
 */
export function buildPreviousTaskDynamicFormExtraState(
  taskDetail: WorkflowTaskDetail | null,
): Record<string, unknown> {
  const previousTask = resolvePreviousTaskFromTaskDetail(taskDetail);
  const id = previousTask?.taskInstanceUniqueId;
  if (id == null || String(id).trim() === "") {
    return {};
  }
  return { taskInstanceUniqueId: String(id) };
}

/**
 * Read-only context form: `hide === true` on the **current** task (legacy; prefer previousTask).
 */
export function taskDetailToHiddenDynamicFormConfig(
  taskDetail: WorkflowTaskDetail | null,
  optionsByField?: OptionsByField,
): TaskDetailDynamicFormConfig {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list) || list.length === 0) {
    return { jsonData: [], initialFormData: {} };
  }

  return buildTaskDetailDynamicFormConfig(list, optionsByField, "readonlyHidden");
}

/**
 * Read-only summary of the prior completed step: `previousTask.propertyValueItemList`
 * rows with `hide !== true`, root `visible !== false`, and
 * `detailsPage.visibility !== false` (same gates as the editable task form).
 */
export function taskDetailToPreviousTaskReadonlyFormConfig(
  taskDetail: WorkflowTaskDetail | null,
  optionsByField?: OptionsByField,
): TaskDetailDynamicFormConfig {
  const list = getPreviousTaskPropertyList(taskDetail);
  if (list.length === 0) {
    return { jsonData: [], initialFormData: {} };
  }

  return buildTaskDetailDynamicFormConfig(list, optionsByField, "readonlyPrevious");
}

export type SourceFieldsReadonlySection = {
  sourceTaskName: string;
  extraState: Record<string, unknown>;
  config: TaskDetailDynamicFormConfig;
};

function isSourceFieldsEnabled(value: { enabled?: boolean } | null): boolean {
  if (!value) return false;
  return value.enabled !== false;
}

/**
 * Read-only prior-step snippets from `sourceFields` (e.g. RI Coordinator
 * Comments, or Review Status / Reject Comments after a reject).
 * One section per `items[]` group that still has visible fields.
 */
export function taskDetailToSourceFieldsReadonlySections(
  taskDetail: WorkflowTaskDetail | null,
  optionsByField?: OptionsByField,
): SourceFieldsReadonlySection[] {
  const sourceFields = resolveSourceFieldsFromTaskDetail(taskDetail);
  if (!isSourceFieldsEnabled(sourceFields) || !Array.isArray(sourceFields?.items)) {
    return [];
  }

  const sections: SourceFieldsReadonlySection[] = [];
  for (const item of sourceFields.items) {
    if (item == null || typeof item !== "object") continue;
    const fields = Array.isArray(item.fields) ? item.fields : [];
    const config = buildTaskDetailDynamicFormConfig(
      fields,
      optionsByField,
      "readonlyPrevious",
    );
    if (config.jsonData.length === 0) continue;

    const uniqueId =
      typeof item.taskInstanceUniqueId === "string"
        ? item.taskInstanceUniqueId.trim()
        : "";
    const sourceTaskName =
      typeof item.sourceTaskName === "string" ? item.sourceTaskName.trim() : "";

    sections.push({
      sourceTaskName,
      extraState: uniqueId ? { taskInstanceUniqueId: uniqueId } : {},
      config,
    });
  }
  return sections;
}

const TASK_OWNERS_SELECTION_PATH = "/pm/selection/task-owners";

/**
 * Re-bucket task owner IDs using the task-owners API id→type map.
 * Backend fieldValue may list an id under taskUsers while the API returns it under taskRoles;
 * CustomSelectGroupWidget only matches option.type === bucket key, so wrong buckets show empty.
 */
export async function resolveTaskOwnersGroupedInitial(
  fieldValue: unknown,
): Promise<Record<string, string[]> | null> {
  const ids = isTaskOwnerStructure(fieldValue)
    ? extractIdsFromTaskOwnerStructure(fieldValue)
    : fieldValueToIds(fieldValue);
  if (ids.length === 0) return null;

  const host = getHostApiBaseUrl().replace(/\/$/, "");
  const url = `${host}${TASK_OWNERS_SELECTION_PATH}`;

  const headers: Record<string, string> = { Accept: "application/json" };
  const auth = getAuthorizationHeader();
  if (auth) headers.Authorization = auth;
  const domain = getDomain();
  if (domain) headers.domain = domain;

  try {
    const res = await fetch(url, { headers, credentials: "include" });
    if (!res.ok) {
      return isTaskOwnerStructure(fieldValue)
        ? (fieldValue as Record<string, string[]>)
        : null;
    }
    const body = await res.json();
    extractTaskOwnersListFromResponse(body);
    return buildTaskOwnerStructureFromIds(ids);
  } catch {
    return isTaskOwnerStructure(fieldValue)
      ? (fieldValue as Record<string, string[]>)
      : null;
  }
}
